<!--
  THE TOP BAR — ≥768px only, and THERE IS NO BAR.

  No plate, no fill, no blur and no border across the top of the screen: the page
  runs underneath this element exactly as if it were not there, and the only thing
  drawn is a row of translucent pills floating on the picture. That is the owner's
  brief in his own words — "everything behind is transparent, like it's literally
  not there, so just bubbles and they're transparent" — and it is why every rule
  below is written the way it is.

  Three zones, and the zones are the point:

    left    you        — the avatar, and the menu of everything that is yours
    centre  the app    — search, then Home · Shows · Movies · My Jovii
    right   the brand  — the logo, which is also the way home

  The grid is `1fr auto 1fr` so the centre is centred against the VIEWPORT and not
  against whatever the two sides happen to weigh. With `space-between` the primary
  navigation drifted left or right as the avatar's name changed length, and the one
  fixed landmark on the screen has to be fixed.

  Search is an ICON that expands into the field in place of the links, because a
  permanent 420px field is the widest thing in the bar and it is used once a session.
  Escape or an empty blur puts the links back; on /search it is expanded and stays.

  AND THE BAR HAS TWO REGISTERS. Everything above describes the bar AT THE TOP
  of the page, and nothing about that resting look may change. Once the reader
  scrolls (class `away`, from scrolled.svelte.ts), the same pills drop to the
  thin `--nav-*-away` glass so they stop blocking the rails — see the scrolled
  register section at the bottom of the styles. A television never enters it.

  FOUR THINGS THAT ARE EASY TO UNDO BY ACCIDENT
   1. `pointer-events: none` on the header and `auto` on the controls. Without it
      the header is a 56px-tall invisible sheet of glass over the top of the hero,
      and "literally not there" is a lie the first time somebody tries to press
      something underneath it.
   2. The header still OCCUPIES its height in the flow. It is `sticky`, not
      `fixed`, so a screen with no hero starts below the pills and only screens
      that opt in (`--chrome-overlap`, which Hero and Title pull up by) run under
      them. Making it `fixed` would put every settings form under the avatar.
   3. The pills carry --nav-*, not --glass-*. --glass-fill is 52% of a light
      surface and disappears over a snow field; --nav-fill is measured against
      pure white. See the note in app.css.
   4. NO HAND-WRITTEN `-webkit-backdrop-filter`. The minifier treats the pair as
      two declarations of one property and keeps the last, which shipped eight
      prefixed-only surfaces and no blur at all in Chromium (OPEN-DEFECTS Q8).
      Write the unprefixed property; the build emits both.
-->
<script lang="ts">
  import AvatarMark from '$lib/social/AvatarMark.svelte';
  import {gate}from './session.svelte';
  import Icon from './Icon.svelte';
  import Logo from './Logo.svelte';
  import SearchField from './SearchField.svelte';
  import { social } from '$lib/social/store';
  import { nav } from './nav.svelte';
  import { scrolled, trackScrolled } from './scrolled.svelte';

  interface Link {
    href: string;
    label: string;
    /** True when this link is the screen you are on. */
    active: (route: typeof nav.route) => boolean;
  }

  /*
   * Five destinations. Friends sits before My Jovii because who you are watching
   * with is chosen more often than what you saved, and the more frequent
   * decision gets the earlier slot. See docs/DESIGN-LANGUAGE.md §4.
   */
  const LINKS: Link[] = [
    { href: '/', label: 'Home', active: (r) => r.name === 'home' },
    { href: '/shows', label: 'Shows', active: (r) => r.name === 'browse' && r.kind === 'series' },
    { href: '/movies', label: 'Movies', active: (r) => r.name === 'browse' && r.kind === 'movie' },
    { href: '/friends', label: 'Friends', active: (r) => r.name === 'friends' },
    { href: '/my', label: 'My Jovii', active: (r) => r.name === 'mine' }
  ];

  const onSearch = $derived(nav.route.name === 'search');
  let expanded = $state(false);
  const showField = $derived(expanded || onSearch);

  // Leaving /search puts the links back, so the bar does not stay in a mode the
  // screen has left. (Arriving AT /search is covered by `onSearch`.)
  $effect(() => {
    if (nav.route.name !== 'search') expanded = false;
  });

  /* The mark the person chose, so the chrome shows THEM and not a stock glyph.
     Null until the social store has answered, which AvatarMark draws as the
     anonymous mark rather than as nothing. The COLOUR is theirs too — the
     account's identity hue, which AvatarMark routes through the theme's
     `--person-*` tokens so this mark recolours live with the theme, exactly
     like the one on the profile page. Accent is only the pre-answer fallback. */
  const socialStore = social();
  const mark = $derived($socialStore.me?.avatar ?? null);
  const tint = $derived($socialStore.me?.color ?? 'var(--accent)');

  /*
   * TWO REGISTERS, ONE CLASS. At the top of the page the bar is exactly what
   * this file always drew. Past a small scroll the pills are sitting on the
   * rails the reader is trying to look at, so the whole row drops to the
   * `--nav-*-away` register — much thinner fills, same blur, the ink shadow
   * doing the fill's old job — and any pill firms straight back up under
   * hover or focus. One boolean from `scrolled.svelte.ts` (rAF-coalesced,
   * refuses to run on a TV), one class flip here, and every actual change is
   * CSS transitioning at --dur. No per-frame style writes anywhere.
   */
  $effect(() => trackScrolled());
</script>

<!--
  `data-dpad-chrome` and deliberately NOT `data-dpad="row"`.

  Marking it chrome keeps the FIRST press of a remote off it: the banner
  underneath is what the screen is for, and a bar of pills that happens to be
  forty pixels higher should not be where a television lands. Everything else
  about it is ordinary — Left and Right walk the pills because they are in a
  line, Down reaches the banner because the banner is underneath.

  Making it a GROUP would have been worse than doing nothing. A group is what
  Up/Down prefer to land in, and this bar is `position: sticky` — its rectangle
  is at the top of the viewport whatever the page has done — so every press of Up
  anywhere on a shelf would have found it before the rail directly above.
-->
<header class="bar" class:away={scrolled.away} data-dpad-chrome>
  <div class="zone left">
    <!--
      YOUR MARK, AND IT GOES STRAIGHT THERE. No menu: there is one destination
      behind this control now (profile and settings are one page), and a
      dropdown holding a single link is a press nobody should have to spend.
      Friends moved into the row on the right, so it is not hiding in here either.

      The mark is the one the person CHOSE, not a generic glyph — square, like
      everything else that stands in for a person in this interface.
    -->
    {#if gate.accounts && gate.state !== 'open'}<a class="guest-signin" href="/signin">Sign in</a>{:else}<a class="you" href="/profile" aria-label="You — your mark, handle and playback settings">
      <AvatarMark avatar={mark} color={tint} size={30} square />
    </a>{/if}
    <a class="about-link" href="/welcome" aria-label="About Jovii"><span class="about-icon"><Icon name="info" size={20}/></span><span class="about-text">About Jovii</span></a>
  </div>

  <nav class="zone centre" aria-label="Primary">
    {#if showField}
      <div class="field">
        <SearchField float autofocus onescape={() => (expanded = false)} />
      </div>
    {:else}
      <button
        type="button"
        class="seek"
        aria-label="Search films and series"
        onclick={() => (expanded = true)}
      >
        <Icon name="search" size={19} />
      </button>
      <ul>
        {#each LINKS as link (link.href)}
          {@const here = link.active(nav.route)}
          <li>
            <a href={link.href} class:here aria-current={here ? 'page' : undefined}>{link.label}</a>
          </li>
        {/each}
      </ul>
    {/if}
  </nav>

  <div class="zone right">
    <!-- The mark alone, and not in a pill. The brand does not need a container
         to be the brand; Netflix's N sits directly on the artwork. -->
    <Logo href="/" size={28} wordmark="auto" />
  </div>
</header>

<style>
  .guest-signin {pointer-events:auto;display:inline-flex;align-items:center;min-height:44px;color:var(--text);background:var(--nav-fill);padding:0 12px;border-radius:999px;font-size:13px;white-space:nowrap;}
  .bar {
    position: sticky;
    top: 0;
    z-index: var(--z-bar);
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    gap: var(--space-3);
    height: calc(var(--bar-h) + var(--safe-t));
    /* The inset keeps the pills out from under the notch and the rounded corners;
       the height carries it so the row still centres in what is left. */
    padding: var(--safe-t) max(var(--page-pad), var(--safe-r)) 0 max(var(--page-pad), var(--safe-l));

    /*
     * NOT A BAR: no background, no border, no blur. Anything added back here is
     * the defect this file exists to fix.
     *
     * And nothing between the pills may catch a press — the page underneath owns
     * every pixel this element does not draw.
     */
    background: none;
    border: 0;
    pointer-events: none;
  }
  /*
   * …and back on for the things that are actually drawn. Leaves and not zones, so
   * the 8px between two pills is the page's and not the bar's.
   *
   * `form` and `[role="menu"]` are in this list because they are drawn surfaces
   * too and were caught being transparent: measured with the menu open, the popup
   * reported `pointer-events: none` while its items reported `auto`, so a press on
   * the popup's own 4px padding fell through to the page behind it — and the
   * outside-pointerdown handler then closed the menu. Same for the search field's
   * 12px inset and its icon.
   */
  .bar :global(a),
  .bar :global(button),
  .bar :global(input),
  .bar :global(form),
  .bar :global([role='menu']) {
    pointer-events: auto;
  }

  .zone {
    display: flex;
    align-items: center;
    min-width: 0;
  }
  .about-link { display:inline-flex; align-items:center; min-height:44px; padding:0 8px; white-space:nowrap; font-size:12px; color:var(--text); text-shadow:var(--nav-ink-shadow); }
  .left {
    gap:4px;
    justify-content: flex-start;
  }
  .centre {
    justify-content: center;
    gap: var(--space-1);
  }
  .right {
    justify-content: flex-end;
  }

  /*
   * ONE BUBBLE, WRITTEN ONCE.
   *
   * Every control in this row is the same object: a translucent pill with a
   * hairline of light along its top edge, floating on the picture. The blur is
   * what makes it read as glass rather than as a grey sticker — three
   * ingredients, and it is plastic without all three.
   *
   * Labels are --text and NOT --text-dim, which is the one thing that changed
   * when the plate went away. --text-dim over --nav-fill composited on white
   * measures 2.2:1; --text measures 5.5:1. A secondary colour is a luxury of
   * knowing what is behind you, and this row never does.
   */
  /*
   * The glass is for the CENTRE only now. The avatar and the mark are bare —
   * they are artwork and a brand mark, both legible in their own right, and a
   * pill around each made three competing containers across one row.
   */
  .seek,
  ul a {
    border: 1px solid rgb(255 255 255 / .32);
    background: rgb(50 50 53 / .56);
    backdrop-filter: blur(20px) saturate(150%);
    box-shadow: inset 0 1px 0 rgb(255 255 255 / .12);
    color: var(--text);
    text-shadow: var(--nav-ink-shadow);
    /* box-shadow and text-shadow joined the list for the away crossfade; the
       whole flip rides one duration so the bar reads as one movement. */
    transition:
      background-color var(--dur) var(--ease),
      border-color var(--dur) var(--ease),
      color var(--dur) var(--ease),
      box-shadow var(--dur) var(--ease),
      text-shadow var(--dur) var(--ease);
  }
  .seek:hover,
  ul a:hover {
    background: var(--nav-fill-hover);
    border-color: var(--glass-line-hi);
  }

  /*
   * Bare, but not unprotected: without a plate behind them these two float over
   * whatever still the hero is showing, so they carry their own separation —
   * a drop shadow on the drawing rather than a fill behind it. Still 44px of
   * hit area; only the paint went away.
   */
  .left .you {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: var(--hit);
    height: var(--hit);
    background: none;
    border: 0;
    box-shadow: none;
    filter: drop-shadow(0 1px 3px rgb(0 0 0 / 0.6));
    transition: opacity var(--dur) var(--ease);
  }
  .left .you:hover {
    opacity: 0.82;
  }
  .right :global(a.logo) {
    filter: drop-shadow(0 1px 3px rgb(0 0 0 / 0.6));
    /* For the away fade — the element has no transition of its own. */
    transition: opacity var(--dur) var(--ease);
  }

  .seek {
    display: grid;
    place-content: center;
    width: var(--hit);
    height: var(--hit);
    flex: none;
    border-radius: var(--radius-pill);
  }

  ul {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    list-style: none;
    margin: 0;
    padding: 0;
  }
  ul a {
    display: grid;
    place-content: center;
    height: var(--hit);
    padding-inline: var(--space-4);
    border-radius: 999px;
    font-size: var(--text-sm);
    font-weight: var(--weight-semi);
    letter-spacing: var(--tracking-snug);
    white-space: nowrap;
  }
  /* The screen you are on. Same shape, tinted — the phone's bubble row says it
     this way already and the two shells must not disagree about what "here" is. */
  ul a.here,
  ul a.here:hover {
    border-color: var(--nav-line-here);
    background: var(--nav-fill-here);
    box-shadow: none;
  }

  /* The logo is a control too — it is the way home — so it is a bubble like the
     rest. Logo.svelte pulls itself 4px outward to sit flush in a bar; there is no
     bar to sit flush in any more, so that comes back. */
  .right :global(a.logo) {
    padding-inline: var(--space-3);
    margin-inline: 0;
  }
  /*
   * "Together" is --text-dim inside Logo.svelte, which is right in a bar and wrong
   * on a bubble: MEASURED on a 245,245,245 ground the pill composites to (92,93,96)
   * and --text-dim reads 2.60:1 there. Two thirds of the way to --text keeps the
   * second word quieter than the first and puts it at 4.8:1.
   */
  .right :global(.word i) {
    color: color-mix(in oklab, var(--text) 80%, var(--text-dim));
  }

  .field {
    width: min(48vw, 420px);
    animation: grow var(--dur) var(--ease-out);
  }
  @keyframes grow {
    from {
      opacity: 0;
      width: 180px;
    }
  }

  /*
   * THE SCROLLED REGISTER. `.away` arrives once the page has left its top
   * (`scrolled.svelte.ts` — one rAF-coalesced boolean, one class flip, all the
   * drawing below is CSS transitioning at --dur) and every pill drops to the
   * thin `--nav-*-away` set: the artwork reads straight through, the blur is
   * untouched — with a fill this thin the blur is most of the legibility — and
   * the strengthened ink shadow carries the letters over the bright stills the
   * fill no longer can. Hover or focus on any one pill snaps THAT pill back to
   * the resting register, so the target under the pointer is always the firm,
   * readable one. A television never gets this class (`trackScrolled` refuses
   * to run under data-tv), and app.css re-pins the away tokens there as the
   * second lock.
   */
  .bar.away .seek,
  .bar.away ul a {
    background: var(--nav-fill-away);
    border-color: var(--nav-line-away);
    box-shadow: var(--nav-shine-away), var(--nav-shadow-away);
    text-shadow: var(--nav-ink-shadow-away);
  }
  /* "Here" stays a step lighter than its neighbours in both registers — the
     luminance cue survives the thinning, just quieter. */
  .bar.away ul a.here {
    background: var(--nav-fill-here-away);
    border-color: var(--nav-line-here-away);
    box-shadow: var(--nav-shine-away), var(--nav-glow-here-away);
  }
  /* The search glyph is an <svg>, which text-shadow cannot reach — it gets the
     same per-drawing scrim the bare marks wear. */
  .bar.away .seek :global(svg) {
    filter: drop-shadow(0 1px 2px rgb(0 0 0 / 0.7));
  }
  /* The pointer (or the focus ring's owner) firms the target back up. */
  .bar.away .seek:hover,
  .bar.away .seek:focus-visible,
  .bar.away ul a:hover,
  .bar.away ul a:focus-visible {
    background: var(--nav-fill-hover-away);
    border-color: var(--nav-line);
    box-shadow: var(--nav-shadow);
    text-shadow: var(--nav-ink-shadow);
  }
  .bar.away ul a.here:hover,
  .bar.away ul a.here:focus-visible {
    background: var(--nav-fill-here);
    border-color: var(--nav-line-here);
    box-shadow: none;
  }
  /* The bare marks have no fill to thin, so they thin themselves — and come
     straight back under the pointer or the ring. */
  .bar.away .left .you,
  .bar.away .right :global(a.logo) {
    opacity: var(--nav-mark-fade);
  }
  .bar.away .left .you:hover,
  .bar.away .left .you:focus-visible,
  .bar.away .right :global(a.logo:hover),
  .bar.away .right :global(a.logo:focus-visible) {
    opacity: 1;
  }

  /* The wordmark and the name are gone at every width now, so the only thing
     left to give back between 768 and ~900 is the padding inside five pills. */
  .about-icon { display:none; }
  @media (max-width: 1050px) {
    .bar { grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr); gap:8px; }
    .about-icon { display:flex; }
    .about-text { display:none; }
    .about-link { min-width:44px; justify-content:center; padding:0; }
    .centre { justify-content:center; }
    .centre > * { flex-shrink:0; }
  }
  @media (max-width: 900px) {
    ul {
      gap: var(--space-1);
    }
    ul a {
      padding-inline: var(--space-3);
    }
  }
</style>
